import React, { useCallback, useEffect, useRef, useState } from "react";
import { PhoneFullScreen } from "../../phone/PhoneShell.jsx";
import { buildRecordSheetModel } from "../../api/recordSheet.js";
import RecordReview from "./RecordReview.jsx";

/**
 * The full inspection record, read only: a sheet over the Plan on a desktop
 * (right-hand panel) and a full-screen sheet on a phone. It shows the record
 * as the register holds it with every amendment applied, all its photos, the
 * approval state with the existing Approve / Reopen buttons, the change
 * history and the NCR raised from it.
 *
 * `load(identity)` resolves api/recordLoad.js#loadRecord's result.
 * `loadPhoto(identity, photo)` resolves { ok, url } for the full-size photo.
 * `onReview(op, view, target)` is the Plan's existing review handler.
 * While the record loads the sheet says so; if the read fails it shows a
 * short error with Retry, never an empty record.
 */

const ERROR_TEXT = {
  not_found: "This record was not found.",
  not_allowed: "You do not have access to this record.",
};

function errorText(code) {
  return ERROR_TEXT[code] || "The record could not be loaded.";
}

function useSheetKeys(closeRef, lightboxOpenRef, panelRef) {
  useEffect(() => {
    const previous = document.activeElement;
    const node = panelRef.current;
    const first = node && (node.querySelector("[data-autofocus]") || node);
    if (first && first.focus) first.focus({ preventScroll: true });
    function onKeyDown(event) {
      if (event.key !== "Escape" || lightboxOpenRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      closeRef.current();
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function Section({ phone, label, children, testId }) {
  return (
    <section className={phone ? "ph-section" : "rs-section"} data-testid={testId}>
      {phone ? <h2 className="ph-label">{label}</h2> : <h3 className="rs-label">{label}</h3>}
      {children}
    </section>
  );
}

function Facts({ phone, rows, testId }) {
  return (
    <dl className={phone ? "ph-dl" : "rs-dl"} data-testid={testId}>
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function Photos({ phone, data, onOpenPhoto, onRetry }) {
  const photos = data.photos;
  return (
    <Section phone={phone} label={`Photos${photos.length ? ` · ${photos.length}` : ""}`} testId="record-photos">
      {data.photosError ? (
        <span className="rs-inline">
          <span data-testid="record-photos-error" className="rs-bad">Could not load the photos.</span>
          <button type="button" className={phone ? "ph-smallbtn" : "btn off rs-small"} onClick={onRetry}>Retry</button>
        </span>
      ) : photos.length ? (
        <div className={phone ? "ph-photos two" : "rs-photos"}>
          {photos.map((p, i) =>
            p.url ? (
              <button
                key={p.key}
                type="button"
                className="rs-photobtn"
                data-testid="record-photo"
                aria-label={`Open photo ${i + 1} of ${photos.length} full size`}
                onClick={() => onOpenPhoto(p, i)}
              >
                <img className={phone ? "ph-photo" : "rs-photo"} src={p.url} alt={p.caption || `Inspection photo ${i + 1}`} />
              </button>
            ) : (
              <span key={p.key} className={phone ? "ph-photo" : "rs-photo rs-photo-miss"} data-testid="record-photo-missing" role="img" aria-label={`Photo ${i + 1} is not available`}>
                Not available
              </span>
            )
          )}
        </div>
      ) : (
        <span className={phone ? "ph-note" : "rs-note"}>No photos recorded</span>
      )}
    </Section>
  );
}

function Changes({ phone, data, model, onRetry }) {
  return (
    <Section phone={phone} label={`Changes${model.amendments.length ? ` · ${model.amendments.length}` : ""}`} testId="record-changes">
      {data.amendmentsError ? (
        <span className="rs-inline">
          <span data-testid="record-changes-error" className="rs-bad">Could not load the changes.</span>
          <button type="button" className={phone ? "ph-smallbtn" : "btn off rs-small"} onClick={onRetry}>Retry</button>
        </span>
      ) : model.amendments.length ? (
        <ol className="rs-changes">
          {model.amendments.map((a) => (
            <li key={a.key} data-testid="record-change">
              <span className="rs-change-head">{a.who}{a.when ? ` · ${a.when}` : ""}</span>
              <span className="rs-change-what">{a.what}{a.notApplied ? ". Not applied, the record kept its earlier value." : "."}</span>
              {a.reason ? <span className="rs-change-reason">Reason: {a.reason}</span> : null}
            </li>
          ))}
        </ol>
      ) : (
        <span className={phone ? "ph-note" : "rs-note"}>No changes since it was recorded.</span>
      )}
    </Section>
  );
}

function Ncr({ phone, data, model, onOpenNcr, onRetry }) {
  if (data.ncrError) {
    return (
      <span className="rs-inline">
        <span data-testid="record-ncr-error" className="rs-bad">NCR status unknown.</span>
        <button type="button" className={phone ? "ph-smallbtn" : "btn off rs-small"} onClick={onRetry}>Retry</button>
      </span>
    );
  }
  if (!model.ncr || !onOpenNcr) return null;
  return (
    <div className="rs-ncr" data-testid="record-ncr">
      <span className="rs-ncr-text">
        <span className="mono rs-ncr-no">NCR D{model.ncr.ncrNumber}</span>
        <span className="rs-ncr-title">{model.ncr.title || "Raised from this inspection"}</span>
      </span>
      <button type="button" className={phone ? "ph-btn sec" : "btn"} data-testid="record-open-ncr" onClick={() => onOpenNcr({ code: model.ncr.code, ncrNumber: model.ncr.ncrNumber })}>
        Open NCR
      </button>
    </div>
  );
}

function Lightbox({ state, onClose, onRetry }) {
  const ref = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    if (ref.current) ref.current.focus({ preventScroll: true });
    function onKeyDown(event) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="rs-light" role="dialog" aria-modal="true" aria-label={`Photo ${state.index + 1} full size`} data-testid="record-lightbox">
      <button ref={ref} type="button" className="btn rs-light-close" data-testid="record-lightbox-close" onClick={onClose}>Close photo</button>
      <div className="rs-light-body">
        {state.status === "loading" ? <span className="rs-note">Loading the photo…</span> : null}
        {state.status === "error" ? (
          <span className="rs-inline">
            <span data-testid="record-lightbox-error" className="rs-bad">Could not open the photo.</span>
            <button type="button" className="btn off rs-small" onClick={onRetry}>Retry</button>
          </span>
        ) : null}
        {state.status === "ready" ? <img className="rs-light-img" src={state.url} alt={state.caption || `Inspection photo ${state.index + 1}`} /> : null}
      </div>
    </div>
  );
}

export default function RecordSheet({ identity, load, loadPhoto, onReview, onOpenNcr, onClose, onRelease, phone = false }) {
  const [state, setState] = useState({ status: "loading" });
  const [photoView, setPhotoView] = useState(null);
  const seq = useRef(0);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const loadRef = useRef(load);
  loadRef.current = load;
  const lightboxOpenRef = useRef(false);
  lightboxOpenRef.current = Boolean(photoView);
  const panelRef = useRef(null);
  useSheetKeys(closeRef, lightboxOpenRef, panelRef);

  const run = useCallback(
    async (quiet) => {
      const mine = ++seq.current;
      if (!quiet) setState({ status: "loading" });
      let result;
      try {
        result = await loadRef.current(identity);
      } catch {
        result = { ok: false, errorCode: "failed" };
      }
      if (mine !== seq.current) return;
      if (quiet && !(result && result.ok)) return; // a failed background re-read keeps what is on screen
      setState(result && result.ok ? { status: "ready", data: result } : { status: "error", errorCode: (result && result.errorCode) || "failed" });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identity.code, identity.inspectionNumber]
  );

  useEffect(() => {
    run(false);
    return () => {
      seq.current += 1;
    };
  }, [run]);
  useEffect(() => () => onRelease && onRelease(), []); // eslint-disable-line react-hooks/exhaustive-deps

  async function openPhoto(photo, index) {
    setPhotoView({ index, caption: photo.caption, photo, status: "loading", url: "" });
    await fetchPhoto(photo, index);
  }
  async function fetchPhoto(photo, index) {
    let outcome;
    try {
      outcome = await loadPhoto(identity, photo);
    } catch {
      outcome = { ok: false, url: "" };
    }
    setPhotoView((cur) => (cur && cur.photo === photo ? { ...cur, index, status: outcome && outcome.ok ? "ready" : "error", url: (outcome && outcome.url) || "" } : cur));
  }

  async function review(op, view, target) {
    const outcome = await onReview(op, view, target);
    // Any answer from the server (done, stale_etag, refused) is followed by a re-read, so the sheet shows the
    // fresh row and etag. Nothing is sent again by itself: the user confirms again.
    if (outcome) await run(true);
    return outcome;
  }

  const model = state.status === "ready" ? buildRecordSheetModel(state.data) : null;
  const retry = () => run(false);
  const titleId = "record-sheet-title";
  const projectText = identity.code;

  const body =
    state.status === "loading" ? (
      <div className="rs-state" role="status" data-testid="record-loading">Loading the record…</div>
    ) : state.status === "error" ? (
      <div className="rs-state" role="alert" data-testid="record-error">
        <span className="rs-bad">{errorText(state.errorCode)}</span>
        <button type="button" className={phone ? "ph-btn sec" : "btn"} data-testid="record-retry" onClick={retry}>Retry</button>
      </div>
    ) : (
      <>
        {model.voided ? <span className="chip bad rs-voided" data-testid="record-voided">Voided</span> : null}
        <Facts phone={phone} rows={model.facts} testId="record-facts" />
        <Section phone={phone} label="Comments" testId="record-comments">
          <p className={phone ? "ph-note" : "rs-comment"}>{model.comments || "No comments recorded."}</p>
        </Section>
        <Section phone={phone} label="Parties present" testId="record-parties">
          <Facts phone={phone} rows={model.parties.map((p) => [p.label, p.value])} />
        </Section>
        <p className="rs-captured" data-testid="record-captured">{model.captured}</p>
        <Photos phone={phone} data={state.data} onOpenPhoto={openPhoto} onRetry={retry} />
        <Section phone={phone} label="Approval" testId="record-approval-section">
          {onReview ? (
            <RecordReview view={model.review} onReview={review} phone={phone} />
          ) : (
            <span data-testid="record-approval" className={phone ? "ph-small" : "rs-note"}>{model.review.approvalText}</span>
          )}
        </Section>
        <Changes phone={phone} data={state.data} model={model} onRetry={retry} />
        <Ncr phone={phone} data={state.data} model={model} onOpenNcr={onOpenNcr} onRetry={retry} />
      </>
    );

  const lightbox = photoView ? (
    <Lightbox
      state={photoView}
      onClose={() => setPhotoView(null)}
      onRetry={() => {
        const cur = photoView;
        setPhotoView({ ...cur, status: "loading" });
        fetchPhoto(cur.photo, cur.index);
      }}
    />
  ) : null;

  if (phone) {
    return (
      <PhoneFullScreen
          title="Inspection record"
          subtitle={model ? `${projectText} · ${model.number}` : undefined}
          titleId={titleId}
          onBack={onClose}
          backLabel="Close record"
          testId="record-sheet"
        >
          {model ? <h1 className="rs-title">{model.title}</h1> : null}
          {body}
          {lightbox}
        </PhoneFullScreen>
    );
  }

  return (
    <div className="rs-overlay" data-testid="record-overlay">
      <div className="rs-backdrop" aria-hidden="true" onClick={() => closeRef.current()} />
      <aside ref={panelRef} className="rs-panel" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} data-testid="record-sheet">
        <header className="rs-head">
          <div className="rs-headtext">
            <span className="mono rs-meta">{model ? `${projectText} · ${model.number}` : projectText}</span>
            <h2 id={titleId} className="rs-title">{model ? model.title : "Inspection record"}</h2>
          </div>
          <button type="button" className="btn" data-autofocus data-testid="record-close" onClick={onClose}>Close</button>
        </header>
        <div className="rs-body">{body}</div>
      </aside>
      {lightbox}
    </div>
  );
}
